/*
  TYOHAAR Rakhi thread — drag the rakhi across, unlock the offer.

  A port of the Raksha Bandhan Claude Design study. The travel span, the
  release threshold, the easing, the arc of the lift and the particle burst
  are that file's, kept to the number:

    start 8%   travel 72%   release past 0.62
    tie 620ms  snap back 450ms   lift -sin(p * PI) * 14px

  ONE NUMBER OF STATE
    `p`, 0 to 1. Position, thread width, lift and the target ring are all
    derived from it, and the only things that write it are a drag, a tween and
    an arrow key. There is no second source of truth to fall out of step.

  WHAT IS NOT A COPY, AND WHY

    No React. The study re-rendered on every pointermove; here `p` goes out as
    a custom property and CSS resolves the four things that depend on it. Same
    positions, no framework, and the drag stays on the compositor.

    The discount is real. The study printed a code as text. This one links to
    Shopify's own /discount/<code> route, which applies the code to the cart
    session — so the shopper never types it and it is already on the order at
    checkout. Liquid builds that href; see the section.

    It remembers. The study reset on every reload, which meant an offer you
    had already earned vanished when you came back. A tie is written to
    localStorage with a timestamp and restored inside the window the merchant
    sets. Never in the theme editor, though — someone arranging this section
    needs to see the untied state, not yesterday's win.
*/

/* The study's numbers. Each is overridden per section; these are fallbacks. */
const TIE_MS = 620;
const SNAP_MS = 450;
const THRESHOLD = 0.62;
const LIFT = 14;
const PARTICLES = 22;

/* Where the rakhi starts and how far it travels, as fractions of the track. */
const START = 0.08;
const TRAVEL = 0.72;

/* Below this, a pointer press is a tap and not a drag. The study's 4px. */
const DRAG_SLOP = 4;

/*
  `Number(x) || fallback` reads zero as missing, and zero is a legitimate
  answer for the lift and the particle count — a merchant turning either off
  would get the default back instead.
*/
function num(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/*
  Like num(), but an EMPTY attribute counts as missing rather than as zero.

  This matters for anything whose zero means "off". Liquid renders a setting it
  cannot find as an empty string, so `data-remember-days=""` reaches here as
  Number('') — which is 0, and finite, so num() hands back 0 and never reaches
  the fallback. The memory then switches itself off with nothing to say it has.

  That is not hypothetical: renaming the setting from hours to days left older
  saved templates without the new key, and every tie stopped being remembered
  while the code looked entirely correct.

  A deliberate "0" typed into the setting still arrives as the string "0" and
  still means zero.
*/
function optNum(value, fallback) {
  if (value === undefined || value === null || String(value).trim() === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

class TyohaarRakhi extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.handle = this.querySelector('[data-handle]');
    this.reveal = this.querySelector('[data-reveal]');
    this.hint = this.querySelector('[data-hint]');
    this.burst = this.querySelector('[data-particles]');
    if (!this.track || !this.handle) return;

    this.tieMs = num(this.dataset.tieMs, TIE_MS);
    this.snapMs = num(this.dataset.snapMs, SNAP_MS);
    this.threshold = num(this.dataset.threshold, THRESHOLD * 100) / 100;
    this.lift = num(this.dataset.lift, LIFT);
    this.count = num(this.dataset.particles, PARTICLES);
    /*
      Seven, not zero, when the attribute is absent: an older saved template
      has no remember_days key, and defaulting to "never remember" turns the
      feature off on exactly the stores that have not been re-saved yet.
    */
    this.rememberMs = optNum(this.dataset.rememberDays, 7) * 86400000;

    /*
      Keyed by the code, so changing the offer offers it again to everyone who
      already claimed the old one — which is what changing the offer means.
    */
    this.storeKey = 'tyohaar:rakhi:' + (this.dataset.code || 'offer');

    this.p = 0;
    this.tied = false;
    this.drag = null;
    this.raf = 0;

    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onDown = this.onDown.bind(this);
    this.onKey = this.onKey.bind(this);

    this.handle.addEventListener('pointerdown', this.onDown);
    this.handle.addEventListener('keydown', this.onKey);

    const tieBtn = this.querySelector('[data-tie]');
    if (tieBtn) tieBtn.addEventListener('click', () => this.tie());

    this.querySelectorAll('[data-retie]').forEach((b) => b.addEventListener('click', () => this.reset()));

    /*
      The claim link is left entirely alone — no handler, no state.

      It used to record the click and swap itself for a different label. The
      swap landed while the page was still on screen, so the button changed
      under the finger that pressed it and the claim appeared to be refused.
      A claim is one thing: hand the code to Shopify's /discount route, which
      puts it on this visitor's cart session, and let it carry them on to the
      collection. The button says the same thing every time.
    */
    this.querySelectorAll('[data-copy]').forEach((b) => b.addEventListener('click', () => this.copy(b)));

    /*
      Read once, not per frame: a media query list is live, and asking it
      inside the tween would be a layout read on every animation frame.
    */
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.paint();
    this.restore();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    this.detach();
    clearTimeout(this.sweep);
    clearTimeout(this.copied);
  }

  /* ------------------------------------------------------------ the number */

  /*
    CSS owns everything that follows from p. The element carries the value and
    the lift; the stylesheet turns them into a position, a thread width and a
    ring. Setting four styles per frame from here would put the same
    arithmetic in two files and let them disagree.
  */
  setP(p) {
    this.p = p;
    this.style.setProperty('--p', p.toFixed(4));
    this.style.setProperty('--lift', (-Math.sin(p * Math.PI) * this.lift).toFixed(2) + 'px');
    this.handle.setAttribute('aria-valuenow', Math.round(p * 100));
  }

  tween(to, dur, after) {
    cancelAnimationFrame(this.raf);

    if (this.reduced || dur <= 0) {
      this.setP(to);
      if (after) after();
      return;
    }

    const from = this.p;
    const t0 = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      /* The study's ease-out cubic. */
      this.setP(from + (to - from) * (1 - Math.pow(1 - k, 3)));
      if (k < 1) this.raf = requestAnimationFrame(step);
      else if (after) after();
    };
    this.raf = requestAnimationFrame(step);
  }

  /* ------------------------------------------------------------- the states */

  tie(options) {
    if (this.tied) return;
    const quiet = options && options.quiet;
    const land = () => {
      this.tied = true;
      this.paint();
      if (!quiet) this.spawn();
      this.remember();
    };
    if (quiet) {
      this.setP(1);
      land();
    } else {
      this.tween(1, this.tieMs, land);
    }
  }

  /*
    Replays the tie. It deliberately does NOT forget.

    It used to clear the stored stamp first. Tap the replay, wander off
    mid-drag, and a tie that had already been earned was gone — the next visit
    showed a fresh prompt as though it had never happened. Re-tying rewrites
    the same stamp anyway, so keeping it costs nothing and abandoning the
    replay now leaves the shopper exactly where they were.
  */
  reset() {
    cancelAnimationFrame(this.raf);
    this.tied = false;
    this.paint();
    if (this.burst) this.burst.textContent = '';
    this.tween(0, this.snapMs);
    /* Back to the thing they can act on, rather than wherever focus was. */
    if (this.handle.isConnected) this.handle.focus({ preventScroll: true });
  }

  paint() {
    this.classList.toggle('is-tied', this.tied);
    if (this.reveal) this.reveal.hidden = !this.tied;
    if (this.hint) this.hint.hidden = this.tied;
    this.handle.setAttribute('aria-disabled', this.tied ? 'true' : 'false');
    this.handle.tabIndex = this.tied ? -1 : 0;
  }

  /* -------------------------------------------------------------- the drag */

  onDown(e) {
    if (this.tied) return;
    e.preventDefault();
    cancelAnimationFrame(this.raf);
    this.drag = { x0: e.clientX, id: e.pointerId, moved: false };
    /*
      On the document, not the handle. A finger that outruns a 66px target
      still owns the gesture, and the release still arrives if it happens
      somewhere else entirely.
    */
    document.addEventListener('pointermove', this.onMove);
    document.addEventListener('pointerup', this.onUp);
    document.addEventListener('pointercancel', this.onUp);
    this.classList.add('is-dragging');
  }

  onMove(e) {
    if (!this.drag || e.pointerId !== this.drag.id) return;
    const r = this.track.getBoundingClientRect();
    if (!r.width) return;
    const from = r.left + r.width * START;
    const span = r.width * TRAVEL;
    if (Math.abs(e.clientX - this.drag.x0) > DRAG_SLOP) this.drag.moved = true;
    this.setP(Math.max(0, Math.min(1, (e.clientX - from) / span)));
  }

  onUp() {
    this.detach();
    const moved = this.drag && this.drag.moved;
    this.drag = null;
    this.classList.remove('is-dragging');

    /*
      A press that never moved is a tap, and a tap ties it. Plenty of people
      will not read "drag" — and on a phone a 66px target invites a tap far
      more than it invites a drag.
    */
    if (!moved) {
      this.tie();
      return;
    }
    if (this.p > this.threshold) this.tie();
    else this.tween(0, this.snapMs);
  }

  detach() {
    document.removeEventListener('pointermove', this.onMove);
    document.removeEventListener('pointerup', this.onUp);
    document.removeEventListener('pointercancel', this.onUp);
  }

  onKey(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      if (!this.tied) this.tie();
      return;
    }
    if (this.tied) return;
    const step = e.key === 'ArrowRight' ? 0.08 : e.key === 'ArrowLeft' ? -0.08 : 0;
    if (!step) return;
    e.preventDefault();
    const p = Math.max(0, Math.min(1, this.p + step));
    if (p >= 1) this.tie();
    else this.setP(p);
  }

  /* --------------------------------------------------------- the celebration */

  spawn() {
    if (!this.burst || this.count <= 0 || this.reduced) return;
    clearTimeout(this.sweep);
    this.burst.textContent = '';

    const frag = document.createDocumentFragment();
    let longest = 0;
    for (let i = 0; i < this.count; i += 1) {
      /* Every third is a petal; the rest are gold. The study's mix. */
      const petal = i % 3 === 0;
      const dur = 1.5 + Math.random() * 1.3;
      const delay = Math.random() * 0.45;
      longest = Math.max(longest, dur + delay);

      const bit = document.createElement('span');
      bit.className = petal ? 'rkh__bit rkh__bit--petal' : 'rkh__bit';
      bit.style.cssText =
        'left:' + (24 + Math.random() * 60).toFixed(1) + '%;' +
        '--size:' + (petal ? 9 + Math.random() * 4 : 4 + Math.random() * 2).toFixed(1) + 'px;' +
        '--dur:' + dur.toFixed(2) + 's;' +
        '--delay:' + delay.toFixed(2) + 's;' +
        '--drift:' + (Math.random() * 70 - 35).toFixed(0) + 'px';
      frag.appendChild(bit);
    }
    this.burst.appendChild(frag);

    /* They finish, then they go. Left in place they are 22 dead nodes. */
    this.sweep = setTimeout(() => {
      if (this.burst) this.burst.textContent = '';
    }, (longest + 0.3) * 1000);
  }

  copy(button) {
    const code = this.dataset.code || '';
    if (!code || !navigator.clipboard) return;
    navigator.clipboard.writeText(code).then(
      () => {
        button.classList.add('is-copied');
        clearTimeout(this.copied);
        this.copied = setTimeout(() => button.classList.remove('is-copied'), 1600);
      },
      () => {}
    );
  }

  /* -------------------------------------------------------------- memory */

  /*
    Every one of these is wrapped: localStorage throws outright in some
    privacy modes rather than returning null, and a thrown getter here would
    take the whole element down before the drag was ever wired up.
  */
  remember() {
    if (!this.rememberMs) return;
    try {
      localStorage.setItem(this.storeKey, String(Date.now()));
    } catch (e) {
      /* Private mode. The tie still holds for this page view. */
    }
  }

  forget() {
    try {
      localStorage.removeItem(this.storeKey);
    } catch (e) {
      /* Nothing was stored. */
    }
  }

  restore() {
    if (!this.rememberMs) return;
    /*
      Never in the editor. Someone arranging this section has to see the
      untied state — restoring yesterday's win would look like the drag is
      broken, and there would be nothing on screen to say otherwise.
    */
    if (window.Shopify && window.Shopify.designMode) return;

    let stamp = null;
    try {
      stamp = localStorage.getItem(this.storeKey);
    } catch (e) {
      return;
    }
    if (!stamp) return;

    const age = Date.now() - Number(stamp);
    if (!Number.isFinite(age) || age < 0 || age > this.rememberMs) {
      this.forget();
      return;
    }

    /* Quiet: no tween, no burst. They already watched it. */
    this.setAttribute('data-restored', '');
    this.tie({ quiet: true });
  }
}

if (!customElements.get('tyoh-rakhi')) customElements.define('tyoh-rakhi', TyohaarRakhi);
